/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Button, ButtonGroup, Dialog } from '@coursera/cds-core';
import { DownloadIcon, ErrorIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { useEnterpriseAdminTrackingData } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseAdminTrackingData';
import StatusCard from 'bundles/enterprise-admin-learners/components/invite-learners/StatusCard';
import { downloadInviteErrorReport } from 'bundles/enterprise-admin-learners/components/invite-learners/downloadInviteErrorReport';
import type { FailedInvite } from 'bundles/enterprise-admin-learners/hooks/useInviteLearners';
import { LEARNERS_BUTTON } from 'bundles/enterprise-admin-learners/utils/eventing';

import _t from 'i18n!nls/enterprise-admin-learners';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  downloadButton: css`
    align-self: flex-start;
    padding-left: 0;
  `,
};

export type NoInvitesSentDialogProps = {
  open: boolean;
  onClose: () => void;
  failed: FailedInvite[];
  /** CSV-only: offers a "Re-upload CSV" action that sends the flow back to the upload step. */
  onReupload?: () => void;
};

/** Shown when every invite in the submission failed — no group was assigned. */
const NoInvitesSentDialog = ({ open, onClose, failed, onReupload }: NoInvitesSentDialogProps) => {
  const failedCount = failed.length;
  const track = useTracker();
  const { enterprise } = useEnterpriseAdminTrackingData();

  return (
    <Dialog open={open} onClose={onClose} width="medium">
      <HeadingGroup>{_t('No invites sent')}</HeadingGroup>
      <Content css={styles.content}>
        {/* Always-present live region so screen readers announce the failure when it appears. */}
        <div role="alert">
          <StatusCard
            title={_t('Skipped')}
            detail={
              failedCount === 1
                ? _t('1 entry had missing or invalid data')
                : _t('#{failedCount} entries had missing or invalid data', { failedCount })
            }
            detailColor="error"
            icon={<ErrorIcon color="error" />}
          />
        </div>
        <Button
          variant="ghost"
          size="small"
          icon={<DownloadIcon />}
          iconPosition="before"
          onClick={() => {
            track('click_button', { button: { name: LEARNERS_BUTTON.DOWNLOAD_INVITE_ERROR_REPORT }, enterprise });
            downloadInviteErrorReport(failed);
          }}
          css={styles.downloadButton}
        >
          {_t('Download error report')}
        </Button>
      </Content>
      <Actions>
        {onReupload ? (
          <ButtonGroup
            primaryButtonProps={{
              children: _t('Re-upload CSV'),
              onClick: () => {
                track('click_button', { button: { name: LEARNERS_BUTTON.INVITE_REUPLOAD_CSV }, enterprise });
                onReupload();
              },
            }}
            alternateButtonProps={{ variant: 'secondary', children: _t('Done'), onClick: onClose }}
          />
        ) : (
          <Button variant="primary" onClick={onClose}>
            {_t('Done')}
          </Button>
        )}
      </Actions>
    </Dialog>
  );
};

export default NoInvitesSentDialog;
